import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronRight,
  Globe,
  Lock,
  Loader2,
  MapPin,
  Compass,
  Info as InfoIcon,
  Trash2,
} from "lucide-react";

/**
 * Phase 11 — Video-level metadata editor. Rendered on top of the project
 * editor when the editor was opened from the library (i.e. `?slug=…` in URL).
 *
 * Consolidates what used to live in the standalone EditModal:
 * - Basic info (title / slug / description / visibility)
 * - Text overlays (Phase 10G)
 * - POI arrows (Phase 10E)
 */
export default function VideoMetadataSection({ videoSlug, onSaved }) {
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openTab, setOpenTab] = useState("info");  // info | text | poi

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [slugInput, setSlugInput] = useState("");
  const [textOverlays, setTextOverlays] = useState([]);
  const [pois, setPois] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/videos/${videoSlug}`);
        if (cancelled) return;
        setVideo(data);
        setTitle(data.title || "");
        setDescription(data.description || "");
        setVisibility(data.visibility || "private");
        setSlugInput(data.slug || "");
        setTextOverlays(Array.isArray(data.text_overlays) ? [...data.text_overlays] : []);
        setPois(Array.isArray(data.poi_annotations) ? [...data.poi_annotations] : []);
      } catch (err) {
        if (!cancelled) setError(formatApiErrorDetail(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [videoSlug]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#2a2a30] bg-[#0f0f12] p-4 flex items-center gap-2 text-[#9a9aa3] text-sm" data-testid="video-meta-loading">
        <Loader2 size={14} className="animate-spin" /> Video wird geladen…
      </div>
    );
  }
  if (error && !video) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-200">{error}</div>
    );
  }
  if (!video) return null;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        title: (title || "").trim() || video.title,
        description,
        visibility,
      };
      if (slugInput.trim() && slugInput.trim() !== video.slug) {
        payload.new_slug = slugInput.trim();
      }
      payload.text_overlays = textOverlays
        .filter((t) => (t.text || "").trim())
        .map((t) => ({
          id: t.id,
          text: (t.text || "").trim().slice(0, 200),
          start_sec: Math.max(0, Number(t.start_sec) || 0),
          duration_sec: Math.max(1, Math.min(15, Number(t.duration_sec) || 4)),
          position: t.position || "bottom-center",
          font_size: t.font_size || "medium",
          style: t.style || "plain",
          text_color: t.text_color || "#FFFFFF",
        }));
      payload.poi_annotations = pois.map((p) => ({
        id: p.id,
        start_sec: Math.max(0, Number(p.start_sec) || 0),
        duration_sec: Math.max(1, Math.min(15, Number(p.duration_sec) || 4)),
        target_yaw_deg: Math.max(-180, Math.min(180, Number(p.target_yaw_deg) || 0)),
        target_pitch_deg: Math.max(-90, Math.min(90, Number(p.target_pitch_deg) || 0)),
        hint_text: (p.hint_text || "").trim() || null,
      }));
      const { data } = await api.patch(`/videos/${video.slug}`, payload);
      toast.success("Video-Daten gespeichert");
      setVideo(data);
      setSlugInput(data.slug);
      onSaved?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err));
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const tabBtn = (id, label, icon) => (
    <button
      key={id}
      type="button"
      onClick={() => setOpenTab(id)}
      className={
        "inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-medium border transition-colors " +
        (openTab === id
          ? "bg-white/10 border-white/20 text-white"
          : "bg-transparent border-transparent text-[#9a9aa3] hover:text-white")
      }
      data-testid={`video-meta-tab-${id}`}
    >
      {icon} {label}
    </button>
  );

  return (
    <div className="rounded-xl border border-[#2a2a30] bg-[#0f0f12] overflow-hidden" data-testid="video-meta-section">
      <div className="px-4 py-3 border-b border-[#2a2a30] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <InfoIcon size={16} className="text-[#ff6b35]" />
          <span className="text-sm font-semibold">Video-Daten &amp; Player-Features</span>
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {tabBtn("info", "Info", <InfoIcon size={12} />)}
          {tabBtn("text", `Text${textOverlays.length ? ` (${textOverlays.length})` : ""}`, <MapPin size={12} />)}
          {tabBtn("poi", `Pfeile${pois.length ? ` (${pois.length})` : ""}`, <Compass size={12} />)}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {openTab === "info" && (
          <div className="space-y-3">
            <LabeledInput label="Titel" value={title} onChange={setTitle} testId="video-meta-title" />
            <LabeledInput label="Link-Name (Slug)" value={slugInput} onChange={setSlugInput} testId="video-meta-slug" />
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Beschreibung</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Optional. Erscheint in WhatsApp/iMessage-Vorschau."
                className="w-full px-3 py-2 rounded-lg bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm resize-none"
                data-testid="video-meta-desc"
              />
              {!description.trim() && (
                <div
                  className="mt-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] leading-snug text-amber-200 flex items-start gap-1.5"
                  data-testid="video-meta-desc-warning"
                >
                  <span aria-hidden="true">⚠️</span>
                  <span>Ohne Beschreibung zeigt WhatsApp nur einen Standard-Text. Trage 2–3 Sätze ein für eine ansprechende Vorschau-Karte.</span>
                </div>
              )}
            </div>
            <div>
              <span className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Sichtbarkeit</span>
              <div className="flex gap-2">
                <VisBtn active={visibility === "public"} onClick={() => setVisibility("public")} icon={<Globe size={12} />} label="Öffentlich" testId="video-meta-vis-public" />
                <VisBtn active={visibility === "private"} onClick={() => setVisibility("private")} icon={<Lock size={12} />} label="Privat" testId="video-meta-vis-private" />
              </div>
            </div>
          </div>
        )}

        {openTab === "text" && (
          <TextOverlaysEditor items={textOverlays} setItems={setTextOverlays} />
        )}

        {openTab === "poi" && (
          <POIEditor items={pois} setItems={setPois} />
        )}

        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}

        <div className="flex items-center justify-end gap-2 pt-1 border-t border-[#2a2a30]/50 -mx-4 -mb-4 px-4 py-3 bg-[#0a0a0d]/40">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white text-sm font-semibold disabled:opacity-50"
            data-testid="video-meta-save"
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            Video-Daten speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-lg bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function VisBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-medium border " +
        (active ? "bg-white/10 border-white/25 text-white" : "bg-transparent border-[#2a2a30] text-[#9a9aa3] hover:text-white")
      }
      data-testid={testId}
    >
      {icon} {label}
    </button>
  );
}

function NumInput({ label, value, onChange, step = 1, min, max, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">{label}</label>
      <input
        type="number" value={value} step={step} min={min} max={max}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full h-9 px-2 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm tabular-nums"
        data-testid={testId}
      />
    </div>
  );
}

function TextOverlaysEditor({ items, setItems }) {
  const upd = (i, key, val) => {
    const n = [...items]; n[i] = { ...n[i], [key]: val }; setItems(n);
  };
  const add = () => setItems([...items, {
    id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    text: "", start_sec: 0, duration_sec: 4,
    position: "bottom-center", font_size: "medium",
    style: "plain", text_color: "#FFFFFF",
  }]);
  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <p className="text-xs text-[#6a6a73]">Noch keine Einblendungen.</p>
      )}
      {items.map((t, i) => (
        <div key={t.id || i} className="rounded-md border border-[#2a2a30] bg-[#17171a] p-2.5 space-y-2" data-testid={`vmeta-text-row-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">Einblendung #{i + 1}</span>
            <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="w-7 h-7 rounded text-[#9a9aa3] hover:text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center" data-testid={`vmeta-text-del-${i}`}>
              <Trash2 size={13} />
            </button>
          </div>
          <textarea
            value={t.text || ""}
            onChange={(e) => upd(i, "text", e.target.value.slice(0, 200))}
            placeholder="Text hier eingeben"
            rows={2}
            className="w-full px-3 py-2 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm resize-none"
            data-testid={`vmeta-text-content-${i}`}
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <NumInput label="Start" value={t.start_sec ?? 0} step={0.5} min={0} onChange={(v) => upd(i, "start_sec", v)} testId={`vmeta-text-start-${i}`} />
            <NumInput label="Dauer" value={t.duration_sec ?? 4} step={0.5} min={1} max={15} onChange={(v) => upd(i, "duration_sec", v)} testId={`vmeta-text-dur-${i}`} />
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Position</label>
              <select value={t.position || "bottom-center"} onChange={(e) => upd(i, "position", e.target.value)} className="w-full h-9 px-2 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm" data-testid={`vmeta-text-pos-${i}`}>
                <option value="top-left">Oben links</option>
                <option value="top-center">Oben Mitte</option>
                <option value="top-right">Oben rechts</option>
                <option value="bottom-left">Unten links</option>
                <option value="bottom-center">Unten Mitte</option>
                <option value="bottom-right">Unten rechts</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Größe</label>
              <select value={t.font_size || "medium"} onChange={(e) => upd(i, "font_size", e.target.value)} className="w-full h-9 px-2 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm">
                <option value="small">Klein</option>
                <option value="medium">Mittel</option>
                <option value="large">Groß</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Stil</label>
              <div className="flex gap-1.5">
                {[["plain", "Nur Text"], ["with_background", "Mit Hintergrund"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => upd(i, "style", v)}
                    className={`flex-1 h-9 rounded-md text-xs font-medium border ${((t.style || "plain") === v) ? "bg-[#1f1f23] border-[#ff6b35] text-white" : "bg-[#1f1f23] border-[#2a2a30] text-[#9a9aa3] hover:text-white"}`}
                    data-testid={`vmeta-text-style-${v}-${i}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Textfarbe</label>
              <div className="flex items-center gap-2 h-9">
                <input type="color" value={t.text_color || "#FFFFFF"}
                  onChange={(e) => upd(i, "text_color", e.target.value.toUpperCase())}
                  className="w-9 h-9 rounded-md border border-[#2a2a30] bg-transparent cursor-pointer" />
                <span className="text-xs font-mono text-[#9a9aa3]">{(t.text_color || "#FFFFFF").toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:border-[#3a3a40] text-xs" data-testid="vmeta-text-add">
        + Einblendung hinzufügen
      </button>
    </div>
  );
}

function POIEditor({ items, setItems }) {
  const upd = (i, key, val) => {
    const n = [...items]; n[i] = { ...n[i], [key]: val }; setItems(n);
  };
  const add = () => setItems([...items, {
    id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    start_sec: 2.0, duration_sec: 4.0,
    target_yaw_deg: 90.0, target_pitch_deg: 0.0, hint_text: "",
  }]);
  return (
    <div className="space-y-3">
      <p className="text-xs text-[#9a9aa3] leading-relaxed">
        Pfeil erscheint im Player wenn der Zuschauer nicht in die Richtung schaut. Yaw:
        <span className="font-mono"> −180 bis 180°</span> (0° vorne, 90° rechts, −90° links, ±180° hinten).
      </p>
      {items.length === 0 && <p className="text-xs text-[#6a6a73]">Noch keine Pfeile.</p>}
      {items.map((p, i) => (
        <div key={p.id || i} className="rounded-md border border-[#2a2a30] bg-[#17171a] p-2.5 space-y-2" data-testid={`vmeta-poi-row-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">Pfeil #{i + 1}</span>
            <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="w-7 h-7 rounded text-[#9a9aa3] hover:text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center" data-testid={`vmeta-poi-del-${i}`}>
              <Trash2 size={13} />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <NumInput label="Start (s)" value={p.start_sec} step={0.5} min={0} onChange={(v) => upd(i, "start_sec", v)} testId={`vmeta-poi-start-${i}`} />
            <NumInput label="Dauer (s)" value={p.duration_sec} step={0.5} min={1} max={15} onChange={(v) => upd(i, "duration_sec", v)} testId={`vmeta-poi-dur-${i}`} />
            <NumInput label="Yaw (°)" value={p.target_yaw_deg} step={5} min={-180} max={180} onChange={(v) => upd(i, "target_yaw_deg", v)} testId={`vmeta-poi-yaw-${i}`} />
            <NumInput label="Pitch (°)" value={p.target_pitch_deg ?? 0} step={5} min={-90} max={90} onChange={(v) => upd(i, "target_pitch_deg", v)} testId={`vmeta-poi-pitch-${i}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#6a6a73] block mb-1">Hinweistext (optional)</label>
            <input value={p.hint_text || ""} onChange={(e) => upd(i, "hint_text", e.target.value.slice(0, 60))}
              placeholder="z. B. Schau nach rechts"
              className="w-full h-9 px-3 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] outline-none text-white text-sm"
              data-testid={`vmeta-poi-hint-${i}`} />
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:border-[#3a3a40] text-xs" data-testid="vmeta-poi-add">
        + Pfeil hinzufügen
      </button>
    </div>
  );
}

/* Suppress unused import lint for icons kept for future extension */
export const __unused = { ChevronDown, ChevronRight };
